import React from 'react';
import type { AdCreative } from './homeTypes';
import { useSponsoredImpression } from './useSponsoredImpression';
import apiService from '../../services/ApiService';
import { resolvePublicFileUrl } from '../../utils/imageUrls';

interface SponsoredCardProps {
  creative: AdCreative;
}

/** Native Discover grid card — clearly labeled Sponsored, never masquerades as a vehicle. */
export const SponsoredCard: React.FC<SponsoredCardProps> = ({ creative }) => {
  const ref = useSponsoredImpression(creative);
  const imageUrl = resolvePublicFileUrl(creative.image_url);
  const clickHref = creative.click_url || apiService.buildAdClickUrl(creative);

  const onClick = () => {
    void apiService.trackEvent('ad_click', {
      metadata: {
        creative_id: creative.creative_id,
        campaign_id: creative.campaign_id,
        placement: creative.placement,
      },
    });
  };

  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      className="vehicle-card flex flex-col h-full border border-white/[0.06] bg-[#0c0c0c]/70 overflow-hidden"
      aria-label={`Sponsored by ${creative.advertiser_name}: ${creative.headline}`}
    >
      <p className="px-3 pt-3 text-[11px] uppercase tracking-[0.12em] text-gray-500">
        Sponsored · {creative.advertiser_name}
      </p>
      <a
        href={clickHref}
        onClick={onClick}
        className="block aspect-[4/3] bg-[#0a0a0a] mt-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={creative.alt_text || creative.headline}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm px-4 text-center">
            {creative.headline}
          </div>
        )}
      </a>
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-white font-medium text-sm leading-snug mb-1">{creative.headline}</h3>
        {creative.body ? (
          <p className="text-gray-400 text-xs leading-relaxed line-clamp-2 mb-3 flex-1">{creative.body}</p>
        ) : (
          <div className="flex-1" />
        )}
        <a
          href={clickHref}
          onClick={onClick}
          className="text-sm text-orange-300 hover:text-orange-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 rounded"
        >
          {creative.cta_label || 'View details'} →
        </a>
      </div>
    </article>
  );
};

export default SponsoredCard;
